import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';


const FORMAT_OPTIONS = {
  'EEEE, d MMMM': { weekday: 'long', day: 'numeric', month: 'long' },
  'HH:mm': { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

type SafeDateFormat = keyof typeof FORMAT_OPTIONS;

@Pipe({
  name: 'safeDate',
  standalone: true
})
export class SafeDatePipe implements PipeTransform {

  private readonly fallbackLocale = inject(LOCALE_ID);

  public transform(
    value: string | number | Date | null | undefined,
    format: SafeDateFormat = 'EEEE, d MMMM',
    timezone?: string,
    locale?: string
  ): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalizedValue = typeof value === 'string' && /^[+-]?\d+$/.test(value.trim())
      ? Number(value)
      : value;
    const date = normalizedValue instanceof Date ? normalizedValue : new Date(normalizedValue);
    if (isNaN(date.getTime())) {
      return null;
    }

    const options: Intl.DateTimeFormatOptions = { ...FORMAT_OPTIONS[format], timeZone: timezone };

    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      try {
        return new Intl.DateTimeFormat(this.fallbackLocale, options).format(date);
      } catch {
        const optionsWithoutTimezone = { ...options };
        delete optionsWithoutTimezone.timeZone;
        return new Intl.DateTimeFormat(this.fallbackLocale, optionsWithoutTimezone).format(date);
      }
    }
  }

}
